import type { ZodType } from "zod";
import z from "zod";

/**
 * The causes an ending may be given now, for one kind of party or another (see
 * `offeredEndingCausesByPartyKind`). `superseded` is handing the involvement
 * over to somebody else.
 */
export type OfferedEndingCause =
  | "superseded"
  | "left-role"
  | "organisation-withdrew"
  | "placement-moved"
  | "no-longer-commissioned"
  | "organisation-closed"
  | "no-longer-needed"
  | "added-in-error"
  | "other";

export const OfferedEndingCauseSchema = z.enum([
  "superseded",
  "left-role",
  "organisation-withdrew",
  "placement-moved",
  "no-longer-commissioned",
  "organisation-closed",
  "no-longer-needed",
  "added-in-error",
  "other",
]) satisfies ZodType<OfferedEndingCause>;

/**
 * Why an involvement ended, from a closed set, so the trail can say what kind
 * of ending it was without carrying the reason, which is prose about a child.
 *
 * Every cause an ending has ever been given, not only those offered now: an
 * ending that has taken effect keeps its cause, so one no longer offered must
 * still be read. `declined`, `sent`, `revoked` and `expired` are never offered.
 * The guest is the author of the first two: `declined` is a guest saying their
 * link was not meant for them, and `sent` is a guest sending the assessment
 * they were asked for, which finishes their part. `revoked` is somebody
 * choosing End on a guest's access, which asks for no reason, so its cause
 * says only that. `expired` is an involvement's end date having passed,
 * taking effect on that date with whoever set it as its author.
 */
export type PersonInvolvementEndedCause =
  | OfferedEndingCause
  | "withdrawn"
  | "case-closed"
  | "declined"
  | "sent"
  | "revoked"
  | "expired";

export const PersonInvolvementEndedCauseSchema = z.enum([
  ...OfferedEndingCauseSchema.options,
  "withdrawn",
  "case-closed",
  "declined",
  "sent",
  "revoked",
  "expired",
]) satisfies ZodType<PersonInvolvementEndedCause>;

/**
 * Why an involvement may not be ended now.
 *
 * - `kept_by_the_record`: who opened the record, a fact about how it began.
 * - `replaced_instead`: whoever runs the case, which is never left without
 *   somebody running it, so the role is handed on rather than ended.
 * - `assessment_open`: ending the designated assessor while a definitive
 *   assessment is under way would leave it with nobody to work on it.
 * - `awaiting_approval`: ending the approver while a definitive assessment
 *   waits for a decision would leave it with nobody to approve it.
 */
export type EndingRefusal =
  | "kept_by_the_record"
  | "replaced_instead"
  | "assessment_open"
  | "awaiting_approval";
